/**
 * TD Access Hook
 *
 * Checks if the current user has TD (Tournament Director) access.
 * TD access requires an OWNER or TD role on at least one club — the same bar
 * as useCanManageClub, so every TD surface agrees on who gets in.
 *
 * Requirements: TD Dashboard 1.1, 1.2
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { useManagedClubs, useCanManageClub } from './useQueries';
import type { MyClub } from '../lib/clubs';

interface UseTDAccessResult {
  /** Whether the user has TD access (can manage at least one club) */
  hasTDAccess: boolean;
  /** Clubs the user can run tournaments for — owned or TD'd */
  managedClubs: MyClub[];
  /** Whether access check is in progress */
  isLoading: boolean;
  /** Error message if access check failed */
  error: string | null;
  /** Refresh the access check */
  refresh: () => Promise<void>;
}

/**
 * Hook to check if user has TD access
 * Uses React Query for caching to avoid duplicate API calls
 */
export function useTDAccess(): UseTDAccessResult {
  const { user, isLoading: authLoading } = useAuth();
  const { managedClubs, isLoading: queryLoading, error, refetch } = useManagedClubs();

  return {
    hasTDAccess: managedClubs.length > 0,
    managedClubs: user ? managedClubs : [],
    isLoading: authLoading || (!!user && queryLoading),
    error: error ? 'Failed to verify TD access' : null,
    refresh: async () => { await refetch(); },
  };
}

interface UseTournamentTDAccessResult {
  /** Whether the user can manage this tournament */
  canManage: boolean;
  /** Whether access check is in progress */
  isLoading: boolean;
  /** Error message if access check failed */
  error: string | null;
}

/**
 * Hook to check if user can manage a specific tournament
 * Requires being the owner of the tournament's club
 * Uses React Query cached data
 */
export function useTournamentTDAccess(clubId: string | undefined): UseTournamentTDAccessResult {
  const { user, isLoading: authLoading } = useAuth();
  const { canManage, isLoading: queryLoading } = useCanManageClub(clubId);

  return {
    canManage: !!user && canManage,
    isLoading: authLoading || (!!user && !!clubId && queryLoading),
    error: null,
  };
}

/**
 * Hook that redirects non-owners away from TD pages
 */
export function useTDAccessGuard(redirectTo: string = '/'): {
  isAuthorized: boolean;
  isLoading: boolean;
} {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { hasTDAccess, isLoading: accessLoading } = useTDAccess();

  useEffect(() => {
    // Wait for auth and access checks to complete
    if (authLoading || accessLoading) return;

    // Redirect if not authenticated
    if (!user) {
      navigate('/sign-in', { replace: true });
      return;
    }

    // Redirect if no TD access
    if (!hasTDAccess) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, hasTDAccess, authLoading, accessLoading, navigate, redirectTo]);

  return {
    isAuthorized: !!user && hasTDAccess,
    isLoading: authLoading || accessLoading,
  };
}
